import { axiosNotLoggedInstance } from "@/services/AuthService";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { format, parseISO } from "date-fns";
import { GdprAssociationsDef, GdprValuesDef } from "@/model/component.model";
import {
  CreateGdprAssociationDto,
  GdprAssociationDto,
  GdprConsentChangePlatform,
  GdprSourcesDTO,
  GdprVersionsDto,
  UuidData
} from "../model/data.model";
import store from "../vuex/store";

export class GdprService {
  convertGdprAssociationsDefToGdprAssociationsDto(
    gdprAssociationsDef: any,
    idRefAddress?: number
  ) {
    const arrayGdpr: GdprValuesDef[] = [];
    const gdprAssociationsDto: GdprAssociationDto[] = [];
    Object.keys(gdprAssociationsDef).map((key: string, index: number) => {
      arrayGdpr.push(gdprAssociationsDef[key]);
    });

    arrayGdpr.forEach(gdpr => {
      const date =
        typeof gdpr.date === "string" ? parseISO(gdpr.date) : gdpr.date;
      const obj = {
        value: gdpr.value,
        associationDateTime: format(date, "dd/MM/yyyy HH:mm"),
        idGdprSource: gdpr.source.value,
        idRefAddress: idRefAddress || gdpr.idRefAddress,
        idGdprElement: gdpr.element
      } as GdprAssociationDto;
      gdprAssociationsDto.push(obj);
    });

    return [...gdprAssociationsDto];
  }

  ///////// UUID \\\\\\\\\\\\\

  verifyUuidValidity(uuid: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/gdpr/uuid/" + uuid
    };
    return axiosNotLoggedInstance
      .request(config)
      .then((res: AxiosResponse<UuidData>) => {
        const isUuidValid = res && res.status === 200;
        return isUuidValid;
      })
      .catch(err => {
        return false;
      });
  }

  getUuidData(uuid: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/gdpr/uuid/" + uuid
    };
    return axiosNotLoggedInstance
      .request(config)
      .then((res: AxiosResponse<UuidData>) => {
        return res.data;
      });
  }

  postUuidData(uuid: string, body: UuidData) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/gdpr/uuid/" + uuid,
      data: body
    };
    return axiosNotLoggedInstance
      .request(config)
      .then((res: AxiosResponse<UuidData>) => {
        return res.data;
      });
  }

  getAssociationsByUuid(uuid: string): Promise<GdprAssociationDto[]> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/shipments/gdpr/uuid/${uuid}/associations`,
      params: {
        uuid
      }
    };
    return axiosNotLoggedInstance.request(config).then(res => {
      return res.data as GdprAssociationDto[];
    });
  }

  createAssociationsByUuid(
    gdprAssociationsDef: GdprAssociationsDef,
    consentChangePlatform: GdprConsentChangePlatform,
    uuid: string,
    idRefAddress?: number
  ): Promise<GdprAssociationDto[]> {
    const gdprAssociationsDto = this.convertGdprAssociationsDefToGdprAssociationsDto(
      gdprAssociationsDef,
      idRefAddress
    );

    const createAssociationData: CreateGdprAssociationDto = {
      gdprAssociationDtos: gdprAssociationsDto,
      consentChangePlatform: consentChangePlatform
    };
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${store.getters.BASE_API_URL}/shipments/gdpr/uuid/${uuid}/createAssociation2`,
      data: createAssociationData
    };
    return axiosNotLoggedInstance.request(config).then(res => {
      return res.data as GdprAssociationDto[];
    });
  }

  getSourcesByUuid(uuid: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + `/shipments/gdpr/uuid/${uuid}/sources`
    };
    return axiosNotLoggedInstance
      .request(config)
      .then((res: AxiosResponse<GdprSourcesDTO>) => {
        return res.data;
      });
  }

  getElementsByUuid(uuid: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + `/shipments/gdpr/uuid/${uuid}/elements`
    };
    return axiosNotLoggedInstance
      .request(config)
      .then((res: AxiosResponse<GdprVersionsDto[]>) => {
        if (!res.data || !res.data.length) return res.data;
        return res.data.sort((a: GdprVersionsDto, b: GdprVersionsDto) => {
          return b.policyVersion - a.policyVersion;
        });
      });
  }

  deleteUuidData(uuid: string, body: UuidData) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: store.getters.BASE_API_URL + "/shipments/gdpr/uuid/" + uuid,
      data: body
    };
    return axiosNotLoggedInstance
      .request(config)
      .then((res: AxiosResponse<UuidData>) => {
        return res.data;
      });
  }
  getPrivacyPolicyFile(fileId: number, tenantId: number) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/shipments/public/gdpr/attachments/privacy-policy/${tenantId}/${fileId}`,
      responseType: "arraybuffer"
    };
    return axiosNotLoggedInstance.request(config).then((res: AxiosResponse) => {
      return res.data as File;
    });
  }
  getAttachment(fileId: number) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/shipments/public/gdpr/attachments/${fileId}`,
      responseType: "arraybuffer"
    };
    return axiosNotLoggedInstance.request(config).then((res: AxiosResponse) => {
      return res.data as ArrayBuffer;
    });
  }
}
